import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Skull, Play, Trash, ShieldCheck, Detective, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const RISK_COLOR = { high: "#FF3B30", medium: "#FF9500", low: "#00F0FF" };

export default function RedTeam() {
  const [actors, setActors] = useState([]);
  const [target, setTarget] = useState("");
  const [actorId, setActorId] = useState("apt29");
  const [objective, setObjective] = useState("");
  const [plans, setPlans] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openStep, setOpenStep] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const [a, p] = await Promise.all([api.get("/redteam/actors"), api.get("/redteam/plans")]);
    setActors(a.data.actors);
    setPlans(p.data.plans);
    setActive((cur) => cur || p.data.plans[0] || null);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (active && active.status === "generating") {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/redteam/plans/${active.id}`);
          setActive(data);
          setPlans((p) => p.map((x) => (x.id === data.id ? data : x)));
          if (data.status !== "generating") {
            clearInterval(pollRef.current);
            if (data.status === "failed") toast.error("Plan generation failed");
            else toast.success("Adversary plan ready");
          }
        } catch (e) {}
      }, 4000);
      return () => clearInterval(pollRef.current);
    }
  }, [active?.id, active?.status]);

  const generate = async (e) => {
    e.preventDefault();
    if (!target.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/redteam/plans", { target: target.trim(), actor_id: actorId, objective: objective.trim() || null });
      setPlans((p) => [data, ...p]);
      setActive(data);
      setTarget(""); setObjective("");
      toast(`Generating ${actors.find((a) => a.id === actorId)?.name} emulation plan…`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/redteam/plans/${id}`);
    setPlans((p) => p.filter((x) => x.id !== id));
    if (active?.id === id) setActive(null);
    toast.success("Plan deleted");
  };

  const plan = active?.plan || {};
  const chain = plan.kill_chain || [];

  return (
    <div data-testid="redteam-page">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]">Adversary Emulation Planner</p>
        <h1 className="font-head text-3xl tracking-tighter font-black mt-1">Red Team</h1>
      </div>

      <form onSubmit={generate} className="bg-[#0C0C14] border border-[#1F1F33] p-5 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3 md:items-end">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Threat Actor</label>
          <select data-testid="redteam-actor-select" value={actorId} onChange={(e) => setActorId(e.target.value)}
            className="w-full bg-[#05050A] border border-[#1F1F33] px-3 py-2.5 text-sm outline-none focus:border-[#FF3B30]">
            {actors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Target</label>
          <input data-testid="redteam-target-input" value={target} onChange={(e) => setTarget(e.target.value)}
            placeholder="Corporate M365 tenant"
            className="w-full bg-[#05050A] border border-[#1F1F33] px-3 py-2.5 text-sm outline-none focus:border-[#FF3B30] placeholder:text-[#71717A]" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Objective</label>
          <input data-testid="redteam-objective-input" value={objective} onChange={(e) => setObjective(e.target.value)}
            placeholder="Exfiltrate crown-jewel data"
            className="w-full bg-[#05050A] border border-[#1F1F33] px-3 py-2.5 text-sm outline-none focus:border-[#FF3B30] placeholder:text-[#71717A]" />
        </div>
        <button data-testid="generate-campaign-button" type="submit" disabled={busy}
          className="flex items-center justify-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-[0.15em] text-[11px] px-5 py-2.5 hover:bg-[#ff5c52] active:scale-[0.98] transition-all disabled:opacity-50">
          <Skull size={14} weight="fill" /> {busy ? "Sending…" : "Generate Plan"}
        </button>
      </form>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-1 space-y-2" data-testid="campaign-list">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Emulation Plans</div>
          {plans.map((p) => (
            <div key={p.id} data-testid={`campaign-${p.id}`}
              className={`p-4 border transition-colors cursor-pointer ${active?.id === p.id ? "border-[#FF3B30] bg-[#FF3B30]/5" : "border-[#1F1F33] bg-[#0C0C14] hover:border-[#33334D]"}`}
              onClick={() => setActive(p)}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-head font-bold text-sm truncate">{p.plan?.plan_name || p.plan?.actor?.name}</span>
                <button data-testid={`delete-plan-${p.id}`} onClick={(e) => { e.stopPropagation(); remove(p.id); }} className="text-[#71717A] hover:text-[#FF3B30]"><Trash size={14} /></button>
              </div>
              <div className="text-[11px] text-[#71717A] mt-1 truncate">{p.target}</div>
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border mt-2 inline-block"
                style={{ color: p.status === "ready" ? "#3ECF8E" : p.status === "failed" ? "#FF3B30" : "#FFCC00", borderColor: "#1F1F33" }}>
                {p.status === "generating" ? "generating…" : p.status}
              </span>
            </div>
          ))}
          {plans.length === 0 && <div className="text-xs text-[#71717A] py-6 text-center border border-dashed border-[#1F1F33]">No plans yet.</div>}
        </div>

        <div className="xl:col-span-2">
          {!active ? (
            <div className="bg-[#0C0C14] border border-dashed border-[#1F1F33] p-16 text-center text-xs text-[#71717A]">Select or generate a plan.</div>
          ) : active.status === "generating" ? (
            <div className="bg-[#0C0C14] border border-[#1F1F33] p-16 text-center">
              <Skull size={32} weight="duotone" color="#FF3B30" className="mx-auto pulse-dot" />
              <p className="text-xs text-[#71717A] mt-3">Mapping ATT&CK kill chain (this can take ~30-60s)…</p>
            </div>
          ) : active.status === "failed" ? (
            <div className="bg-[#0C0C14] border border-[#FF3B30]/40 p-8 text-center" data-testid="campaign-error">
              <Warning size={28} color="#FF3B30" className="mx-auto" />
              <p className="text-sm text-[#FF3B30] mt-2">Generation failed</p>
              <p className="text-[11px] text-[#71717A] mt-1">{active.error}</p>
            </div>
          ) : (
            <div className="bg-[#0C0C14] border border-[#1F1F33] p-5" data-testid="campaign-detail">
              <h2 className="font-head text-xl font-bold tracking-tight">{plan.plan_name}</h2>
              <div className="flex items-center gap-3 mt-1 text-[11px] text-[#71717A] flex-wrap">
                <span className="text-[#FF9500]">{plan.actor?.name}</span>
                <span>Target: {plan.target}</span>
                {plan.risk_level && <span className="uppercase px-1.5 border" style={{ color: RISK_COLOR[plan.risk_level], borderColor: "#1F1F33" }}>{plan.risk_level} risk</span>}
                {plan.estimated_duration_days && <span>~{plan.estimated_duration_days}d</span>}
              </div>
              {plan.objective && <p className="text-xs text-[#A1A1AA] mt-3">{plan.objective}</p>}

              {plan.assumptions?.length > 0 && (
                <div className="mt-3 text-[11px] text-[#71717A]">
                  <span className="uppercase tracking-wider text-[#71717A]">Assumptions: </span>{plan.assumptions.join(" · ")}
                </div>
              )}

              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mt-5 mb-3">Kill Chain ({chain.length} steps)</div>
              <div className="relative pl-6">
                <div className="absolute left-[9px] top-1 bottom-1 w-px bg-[#1F1F33]" />
                {chain.map((s) => (
                  <div key={s.step} className="relative mb-4" data-testid={`phase-${s.step}`}>
                    <div className="absolute -left-6 top-0.5 w-[18px] h-[18px] rounded-full bg-[#FF3B30]/20 border border-[#FF3B30] flex items-center justify-center text-[9px] text-[#FF3B30] font-bold">{s.step}</div>
                    <button onClick={() => setOpenStep(openStep === s.step ? null : s.step)} className="text-left w-full">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-[0.12em] text-[#FF9500]">{s.phase}</span>
                        {(s.techniques || []).map((t) => <span key={t.id} className="text-[9px] font-mono px-1.5 py-0.5 border border-[#1F1F33] text-[#71717A]">{t.id} {t.name}</span>)}
                      </div>
                      <div className="text-sm mt-0.5">{s.description}</div>
                    </button>
                    {openStep === s.step && (
                      <div className="mt-2 text-[11px] space-y-2 fade-up">
                        {s.tools?.length > 0 && <div className="text-[#71717A]"><span className="text-[#A1A1AA]">Tools:</span> {s.tools.join(", ")}</div>}
                        {s.expected_artifacts?.length > 0 && <div className="text-[#71717A]"><span className="text-[#A1A1AA]">Artifacts:</span> {s.expected_artifacts.join(", ")}</div>}
                        {s.detection?.sigma && (
                          <div className="border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-2">
                            <div className="flex items-center gap-1 text-[10px] uppercase text-[#00F0FF] mb-1"><Detective size={12} /> Sigma Detection · {s.detection.sensor}</div>
                            <pre className="whitespace-pre-wrap text-[10px] text-[#A1A1AA] font-mono max-h-40 overflow-y-auto">{s.detection.sigma}</pre>
                          </div>
                        )}
                        {s.detection?.yara && (
                          <div className="border border-[#FFCC00]/30 bg-[#FFCC00]/5 p-2">
                            <div className="text-[10px] uppercase text-[#FFCC00] mb-1">YARA</div>
                            <pre className="whitespace-pre-wrap text-[10px] text-[#A1A1AA] font-mono max-h-40 overflow-y-auto">{s.detection.yara}</pre>
                          </div>
                        )}
                        {s.mitigations?.length > 0 && (
                          <div className="flex items-start gap-1 text-[#3ECF8E]"><ShieldCheck size={13} className="mt-0.5 shrink-0" /><span>{s.mitigations.join(" · ")}</span></div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {plan.safety_notes?.length > 0 && (
                <div className="mt-4 border-t border-[#1F1F33] pt-3 text-[11px] text-[#FF9500] flex items-start gap-2">
                  <Warning size={14} className="mt-0.5 shrink-0" /><span>{plan.safety_notes.join(" · ")}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
